import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const AUTH_IMG =
  "https://images.unsplash.com/photo-1640357960494-9242650846d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxkYXJrJTIwbHV4dXJ5JTIwaW50ZXJpb3IlMjBmdXJuaXR1cmV8ZW58MHx8fHwxNzkxNzYyOTI4fDA&ixlib=rb-4.1.0&q=85";

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", surname: "", email: "", password: "", newsletter: false });

  const submitLogin = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success("Bentornato nell'atelier");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await register(regForm.name, regForm.surname, regForm.email, regForm.password, regForm.newsletter);
      toast.success("Account creato. Benvenuto da AB fëlise.");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen grid lg:grid-cols-2" data-testid="auth-page">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={AUTH_IMG} alt="Atelier AB fëlise" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute bottom-16 left-12 right-12">
          <p className="font-serif italic font-light text-3xl text-white/85 leading-snug">
            «Ogni oggetto che scegli
            <br />
            diventa parte della tua storia.»
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 md:px-12 pt-28 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          <p className="overline-label mb-5">Area riservata</p>
          <h1 className="font-serif font-light text-4xl md:text-5xl mb-12">
            Il tuo <span className="italic text-primary">account</span>
          </h1>

          <Tabs defaultValue="login" onValueChange={() => setError("")}>
            <TabsList className="bg-secondary border border-white/10 w-full mb-10">
              <TabsTrigger value="login" className="flex-1 text-xs uppercase tracking-[0.2em]" data-testid="tab-login">
                Accedi
              </TabsTrigger>
              <TabsTrigger value="register" className="flex-1 text-xs uppercase tracking-[0.2em]" data-testid="tab-register">
                Registrati
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={submitLogin} className="space-y-6" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</Label>
                  <Input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    required
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className="bg-secondary border-white/10 h-13 py-4 focus-visible:ring-primary"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Password</Label>
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type="password"
                    required
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    className="bg-secondary border-white/10 h-13 py-4 focus-visible:ring-primary"
                  />
                </div>
                {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="login-submit-button"
                  className="w-full h-14 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300 disabled:opacity-50"
                >
                  {busy ? "Accesso…" : "Accedi"}
                </button>
                <p className="text-center">
                  <Link
                    to="/password-dimenticata"
                    data-testid="forgot-password-link"
                    className="text-xs text-muted-foreground hover:text-primary transition-colors tracking-wide"
                  >
                    Hai dimenticato la password?
                  </Link>
                </p>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={submitRegister} className="space-y-6" data-testid="register-form">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="reg-name" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Nome</Label>
                    <Input
                      id="reg-name"
                      data-testid="register-name-input"
                      required
                      value={regForm.name}
                      onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                      className="bg-secondary border-white/10 h-13 py-4 focus-visible:ring-primary"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-surname" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Cognome</Label>
                    <Input
                      id="reg-surname"
                      data-testid="register-surname-input"
                      required
                      value={regForm.surname}
                      onChange={(e) => setRegForm({ ...regForm, surname: e.target.value })}
                      className="bg-secondary border-white/10 h-13 py-4 focus-visible:ring-primary"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-email" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</Label>
                  <Input
                    id="reg-email"
                    data-testid="register-email-input"
                    type="email"
                    required
                    value={regForm.email}
                    onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                    className="bg-secondary border-white/10 h-13 py-4 focus-visible:ring-primary"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-password" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Password</Label>
                  <Input
                    id="reg-password"
                    data-testid="register-password-input"
                    type="password"
                    required
                    minLength={6}
                    value={regForm.password}
                    onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                    className="bg-secondary border-white/10 h-13 py-4 focus-visible:ring-primary"
                  />
                </div>
                <label className="flex items-start gap-3 cursor-pointer pt-2" data-testid="newsletter-checkbox-label">
                  <Checkbox
                    data-testid="register-newsletter-checkbox"
                    checked={regForm.newsletter}
                    onCheckedChange={(v) => setRegForm({ ...regForm, newsletter: !!v })}
                    className="border-white/20 data-[state=checked]:bg-primary data-[state=checked]:border-primary mt-0.5"
                  />
                  <span className="text-sm text-muted-foreground leading-relaxed">
                    Iscriviti alla newsletter: ricevi aggiornamenti su promozioni e nuovi prodotti
                  </span>
                </label>
                {error && <p className="text-sm text-destructive" data-testid="auth-error-register">{error}</p>}
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="register-submit-button"
                  className="w-full h-14 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300 disabled:opacity-50"
                >
                  {busy ? "Creazione…" : "Crea account"}
                </button>
              </form>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </main>
  );
}
